/**
 * Price Range Slider — Shopify Dawn 15.4.0
 * File: assets/price-range-slider.js
 *
 * Initialises dual-handle range sliders inside Dawn's facet filters.
 * Works with Dawn's existing facets.js — it reads the same hidden
 * number inputs so the form submission is unchanged.
 */

class PriceRangeSlider {
  /**
   * @param {HTMLElement} wrapper  – [data-price-range-filter]
   */
  constructor(wrapper) {
    this.wrapper      = wrapper;
    this.sliderWrap   = wrapper.querySelector('[data-range-min]');
    if (!this.sliderWrap) return;

    this.rangeMin     = parseFloat(this.sliderWrap.dataset.rangeMin)  || 0;
    this.rangeMax     = parseFloat(this.sliderWrap.dataset.rangeMax)  || 1000;
    this.valueMin     = parseFloat(this.sliderWrap.dataset.valueMin)  || this.rangeMin;
    this.valueMax     = parseFloat(this.sliderWrap.dataset.valueMax)  || this.rangeMax;
    this.moneyFormat  = this.sliderWrap.dataset.moneyFormat || '{{amount}}';
    this.currSymbol   = this.sliderWrap.dataset.currencySymbol || '$';

    // DOM refs
    this.thumbMin     = this.sliderWrap.querySelector('[data-range-thumb="min"]');
    this.thumbMax     = this.sliderWrap.querySelector('[data-range-thumb="max"]');
    this.trackFill    = this.sliderWrap.querySelector('[data-track-fill]');
    this.inputMin     = wrapper.querySelector('[data-price-input="min"]');
    this.inputMax     = wrapper.querySelector('[data-price-input="max"]');
    this.displayMin   = wrapper.querySelector('[data-price-display="min"]');
    this.displayMax   = wrapper.querySelector('[data-price-display="max"]');
    this.labelMin     = this.sliderWrap.querySelector('[data-thumb-label="min"]');
    this.labelMax     = this.sliderWrap.querySelector('[data-thumb-label="max"]');
    this.thumbElemMin = this.sliderWrap.querySelector('[data-thumb="min"]');
    this.thumbElemMax = this.sliderWrap.querySelector('[data-thumb="max"]');

    this._init();
  }

  _init() {
    // Set initial range input values
    this.thumbMin.min   = this.rangeMin;
    this.thumbMin.max   = this.rangeMax;
    this.thumbMin.value = this.valueMin;

    this.thumbMax.min   = this.rangeMin;
    this.thumbMax.max   = this.rangeMax;
    this.thumbMax.value = this.valueMax;

    // Bind events
    this.thumbMin.addEventListener('input', () => this._onMinChange());
    this.thumbMax.addEventListener('input', () => this._onMaxChange());

    // Dawn's facets.js triggers form submit on input change — hook into it
    this.thumbMin.addEventListener('change', () => this._syncHiddenInputs(true));
    this.thumbMax.addEventListener('change', () => this._syncHiddenInputs(true));

    this._updateUI();
  }

  _onMinChange() {
    const min = Math.min(parseFloat(this.thumbMin.value), parseFloat(this.thumbMax.value) - 1);
    this.thumbMin.value = min;
    this.valueMin = min;
    this._syncHiddenInputs(false);
    this._updateUI();
  }

  _onMaxChange() {
    const max = Math.max(parseFloat(this.thumbMax.value), parseFloat(this.thumbMin.value) + 1);
    this.thumbMax.value = max;
    this.valueMax = max;
    this._syncHiddenInputs(false);
    this._updateUI();
  }

  /**
   * Keep the hidden number inputs (used by Dawn's facets.js) in sync.
   * @param {boolean} fireEvent  If true, dispatch 'change' so Dawn auto-submits.
   */
  _syncHiddenInputs(fireEvent) {
    const min = parseFloat(this.thumbMin.value);
    const max = parseFloat(this.thumbMax.value);

    if (this.inputMin) {
      this.inputMin.value = min <= this.rangeMin ? '' : min;
      if (fireEvent) this.inputMin.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (this.inputMax) {
      this.inputMax.value = max >= this.rangeMax ? '' : max;
      if (fireEvent) this.inputMax.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  _updateUI() {
    const min      = parseFloat(this.thumbMin.value);
    const max      = parseFloat(this.thumbMax.value);
    const span     = this.rangeMax - this.rangeMin;
    const leftPct  = ((min - this.rangeMin) / span) * 100;
    const rightPct = ((this.rangeMax - max) / span) * 100;

    // Track fill
    if (this.trackFill) {
      this.trackFill.style.left  = leftPct + '%';
      this.trackFill.style.right = rightPct + '%';
    }

    // Price boxes — plain integer, thousand-separated, no currency symbol
    // (currency symbol is a separate <span class="prs__currency"> in the template)
    const fmt = (n) => Math.round(n).toLocaleString('en-IN');
    if (this.displayMin) this.displayMin.textContent = fmt(min);
    if (this.displayMax) this.displayMax.textContent = fmt(max);
  }

  /**
   * Format a display-unit amount to a money string.
   *
   * IMPORTANT: values arrive here already in display units (e.g. 3680, not 368000)
   * because the Liquid snippet uses | money_without_currency which converts cents
   * → display amount before writing the data attribute / range input value.
   * Do NOT divide by 100.
   */
  _formatMoney(amount) {
    const format = this.moneyFormat || '{{amount}}';

    // Amount is already the display number (e.g. 3680 for ₹3,680)
    const withDecimals   = (n) => parseFloat(n).toFixed(2).replace(/\.00$/, '');
    const noDecimals     = (n) => Math.round(n).toString();
    const withDelimiters = (str) => str.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    let formatted = format
      .replace('{{amount}}',                              withDelimiters(withDecimals(amount)))
      .replace('{{amount_no_decimals}}',                  withDelimiters(noDecimals(amount)))
      .replace('{{amount_with_comma_separator}}',         withDecimals(amount).replace('.', ','))
      .replace('{{amount_no_decimals_with_comma_separator}}',
        noDecimals(amount).replace(/\B(?=(\d{3})+(?!\d))/g, '.'));

    // Fallback: format didn't contain a known placeholder
    if (formatted === format) {
      formatted = this.currSymbol + withDelimiters(withDecimals(amount));
    }
    return formatted;
  }
}

/* ── Auto-initialise all sliders on the page ───────────────── */
function initPriceRangeSliders() {
  document.querySelectorAll('[data-price-range-filter]').forEach((el) => {
    if (!el._priceRangeSlider) {
      el._priceRangeSlider = new PriceRangeSlider(el);
    }
  });
}

// Init on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPriceRangeSliders);
} else {
  initPriceRangeSliders();
}

// Re-init after Dawn re-renders facets (ajax filter updates)
document.addEventListener('facets:updated', initPriceRangeSliders);  // custom event
document.addEventListener('shopify:section:load', initPriceRangeSliders);

// Also observe DOM mutations in case Dawn injects the facet HTML dynamically
const _observer = new MutationObserver((mutations) => {
  for (const m of mutations) {
    for (const node of m.addedNodes) {
      if (node.nodeType !== 1) continue;
      if (
        node.matches?.('[data-price-range-filter]') ||
        node.querySelector?.('[data-price-range-filter]')
      ) {
        initPriceRangeSliders();
        return;
      }
    }
  }
});
_observer.observe(document.body, { childList: true, subtree: true });
